import * as Sentry from '@sentry/react'

interface User {
  id: string
  username: string
  email: string
}

interface ExtraTags {
  [key: string]: string | number | boolean
}

interface CaptureCustomErrorParams {
  message: string
  user?: User
  extra?: any
  route?: string
  extraTags?: ExtraTags
  level?: 'error' | 'warning' | 'info' | 'debug' | 'fatal'
  error?: Error
}

interface CaptureCustomLogParams {
  message: string
  level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal'
  user?: User
  extra?: any
  route?: string
  extraTags?: ExtraTags
}

const { logger } = Sentry

const captureCustomError = ({
  message,
  user,
  extra,
  route,
  extraTags,
  level = 'error',
  error,
}: CaptureCustomErrorParams): void => {
  if (user) {
    Sentry.setUser(user)
    Sentry.setTags({
      userId: user.id,
      userEmail: user.email,
      username: user.username,
    })
  }

  if (extraTags) {
    Sentry.setTags(extraTags)
  }

  if (route) {
    Sentry.setTag('route', route)
    Sentry.setContext('route', { currentRoute: route })
  }

  const logData = {
    message,
    route,
    user: user ? { id: user.id, email: user.email } : undefined,
    extra,
    ...extraTags,
  }

  switch (level) {
    case 'fatal':
      logger.fatal(message, logData)
      break
    case 'error':
      logger.error(message, logData)
      break
    case 'warning':
      logger.warn(message, logData)
      break
    case 'info':
      logger.info(message, logData)
      break
    case 'debug':
      logger.debug(message, logData)
      break
    default:
      logger.error(message, logData)
  }

  if (error instanceof Error) {
    Sentry.captureException(error, {
      level,
      tags: {
        priority:
          level === 'fatal'
            ? 'critical'
            : level === 'error'
              ? 'high'
              : 'medium',
        route,
        errorType: 'custom',
        ...extraTags,
      },
      user,
      extra: {
        originalMessage: message,
        ...extra,
      },
    })
  } else {
    Sentry.captureMessage(message, {
      level,
      tags: {
        priority:
          level === 'fatal'
            ? 'critical'
            : level === 'error'
              ? 'high'
              : 'medium',
        route,
        messageType: 'custom',
        ...extraTags,
      },
      user,
      extra,
    })
  }
}

const captureCustomLog = ({
  message,
  level,
  user,
  extra,
  route,
  extraTags,
}: CaptureCustomLogParams): void => {
  const logData = {
    route,
    user: user ? { id: user.id, email: user.email } : undefined,
    extra,
    ...extraTags,
  }

  switch (level) {
    case 'trace':
      logger.trace(message, logData)
      break
    case 'debug':
      logger.debug(message, logData)
      break
    case 'info':
      logger.info(message, logData)
      break
    case 'warn':
      logger.warn(message, logData)
      break
    case 'error':
      logger.error(message, logData)
      break
    case 'fatal':
      logger.fatal(message, logData)
      break
  }
}

const capturePerformanceLog = (
  operation: string,
  duration: number,
  extra?: any,
  route?: string,
): void => {
  logger.info(
    logger.fmt`Performance: ${operation} completed in ${duration}ms`,
    {
      operation,
      duration,
      route,
      type: 'performance',
      ...extra,
    },
  )

  if (duration > 3000) {
    logger.warn(
      logger.fmt`Slow operation detected: ${operation} took ${duration}ms`,
      {
        operation,
        duration,
        route,
        type: 'performance-warning',
        ...extra,
      },
    )
  }
}

const captureBusinessEvent = (
  event: string,
  data: any,
  user?: User,
  route?: string,
): void => {
  logger.info(logger.fmt`Business event: ${event}`, {
    event,
    data,
    user: user ? { id: user.id, email: user.email } : undefined,
    route,
    type: 'business-event',
  })
}

const captureApiError = (
  endpoint: string,
  method: string,
  status: number,
  error: string,
  user?: User,
  extra?: any,
): void => {
  const isClientError = status >= 400 && status < 500
  const isServerError = status >= 500

  const level = isServerError ? 'error' : isClientError ? 'warning' : 'info'

  captureCustomError({
    message: `API ${method} ${endpoint} failed with status ${status}`,
    level,
    user,
    route: endpoint,
    extraTags: {
      apiEndpoint: endpoint,
      httpMethod: method,
      httpStatus: status,
      errorType: 'api',
      isClientError,
      isServerError,
    },
    extra: {
      errorMessage: error,
      ...extra,
    },
  })
}

export {
  captureApiError,
  captureBusinessEvent,
  captureCustomError,
  captureCustomLog,
  capturePerformanceLog,
}

export default captureCustomError
